/**
 * SitePick front — 「自社の情報で見る」の状態（docs/22 §4.3・§3.6）。
 *
 * 置き換えた内容は **親のメモリの中だけ** に持つ（サーバへ送らない・ブラウザの保存領域にも書かない）。
 * デモの中でページを移っても親は残るので消えないが、ページを読み込み直すと消える（docs/22 §12-6 (a)）。
 * 色の選択だけは既存のカラー切替（live-colors.js / color-store.js）が持つので、ここでは扱わない。
 *
 * デモへ送る形（`apply.state`）は試作（PoC）と同じ:
 *   { company, texts: { ref: rows[] }, text_sizes: { ref: "s"|"l" }, logo: { asset, width } | null,
 *     images: { ref: assetId }, sections: { order, hidden, clones }, items: { added, hidden } }
 * ページの構成（sections / items）は A2 で扱う。A1 では常に空のまま送る。
 */

/**
 * @returns {{company: string, texts: Object<string, string[]>, text_sizes: Object<string, string>, logo: ({asset: string, width: number}|null), images: Object<string, string>}}
 */
function emptyData() {
  return {
    company: "",
    texts: Object.create(null),
    text_sizes: Object.create(null),
    logo: null,
    images: Object.create(null),
  };
}

/**
 * 状態を作る。
 *
 * @returns {Object}
 */
export function createState() {
  let data = emptyData();
  /** 画像の本体（asset id -> { blob, type, bytes, width, height }）。参照されなくなったら捨てる。 */
  let assets = new Map();

  const gcAssets = () => {
    const used = new Set(Object.values(data.images));
    if (data.logo && data.logo.asset) {
      used.add(data.logo.asset);
    }
    for (const id of [...assets.keys()]) {
      if (!used.has(id)) {
        assets.delete(id);
      }
    }
  };

  return {
    get data() {
      return data;
    },

    /**
     * 変更を加える（呼び出し側が data を直接書き換える）。終わったら使われない画像を捨てる。
     *
     * @param {(d: Object) => void} mutate
     */
    commit(mutate) {
      mutate(data);
      gcAssets();
    },

    /** すべて空に戻す。 */
    reset() {
      data = emptyData();
      assets = new Map();
    },

    /**
     * @param {string} id
     * @param {{blob: Blob, type: string, bytes: number, width: number, height: number}} asset
     */
    putAsset(id, asset) {
      assets.set(id, asset);
    },

    /**
     * @param {string} id
     * @returns {({blob: Blob, type: string, bytes: number, width: number, height: number}|undefined)}
     */
    asset(id) {
      return assets.get(id);
    },

    /** @returns {Array<[string, Object]>} */
    assetEntries() {
      return [...assets.entries()];
    },

    /** @returns {number} 画像の合計バイト数 */
    assetBytes() {
      let n = 0;
      for (const a of assets.values()) {
        n += a.bytes || 0;
      }
      return n;
    },

    /** @returns {boolean} 何も置き換えていないか */
    isEmpty() {
      return !data.company && !data.logo && !Object.keys(data.texts).length && !Object.keys(data.text_sizes).length && !Object.keys(data.images).length;
    },

    /** デモへ送る形（宣言的な全状態）。 */
    toWire() {
      const texts = {};
      for (const [ref, rows] of Object.entries(data.texts)) {
        texts[ref] = rows.slice();
      }
      return {
        company: data.company,
        texts,
        text_sizes: Object.assign({}, data.text_sizes),
        logo: data.logo ? { asset: data.logo.asset, width: data.logo.width } : null,
        images: Object.assign({}, data.images),
        // A2（ページの構成）まで常に空。
        sections: { order: [], hidden: [], clones: [] },
        items: { added: [], hidden: [] },
      };
    },
  };
}
